import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import PrerequisiteGate from '../components/PrerequisiteGate';
import AutofillBanner from '../components/AutofillBanner';
import { useUserContext } from '../hooks/useUserContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { FileText, Loader2, Image, Video, Lightbulb, Hash, Eye, MessageSquare, RefreshCw, Sliders } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import AIRefinementPanel from '../components/AIRefinementPanel';
import ElevationMoment from '../components/ElevationMoment';
import { useTranslation } from 'react-i18next';
import StepReviewModal from '../components/StepReviewModal';

const ContentGeneratorContent = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [contentType, setContentType] = useState('post');
  const [generatedContent, setGeneratedContent] = useState(null);
  const [contentRemaining, setContentRemaining] = useState(null);
  const [history, setHistory] = useState([]);
  const [showRefinementPanel, setShowRefinementPanel] = useState(false);
  const [showElevationMoment, setShowElevationMoment] = useState(false);
  const [generationCount, setGenerationCount] = useState(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  
  // Auto-fill context
  const { 
    hasData: hasContextData, 
    sources, 
    sourcesDisplay, 
    summaryMessage 
  } = useUserContext();

  const tier = user?.subscription_tier || 'free';

  useEffect(() => {
    fetchContentRemaining();
    fetchHistory();
  }, []);

  const fetchContentRemaining = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/content/remaining`, { headers });
      setContentRemaining(response.data);
    } catch (error) {
      console.error('Error fetching content remaining:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/content/history`, { headers });
      setHistory(response.data.content);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleGenerate = async () => {
    if (contentRemaining && contentRemaining.remaining <= 0) {
      toast.error('Limite mensuelle atteinte. Passe à un niveau supérieur pour plus d\'idées !');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/content/generate`,
        { content_type: contentType },
        { headers }
      );
      setGeneratedContent(response.data);
      toast.success('Idée de contenu générée !');
      fetchContentRemaining();
      fetchHistory();
      
      // Show review modal after first generation
      const newCount = generationCount + 1;
      setGenerationCount(newCount);
      
      // Show step review modal on first generation
      if (newCount === 1) {
        setShowReviewModal(true);
      }
      
      // Show elevation moment after every 3rd generation for Essentiel users
      // or after 1st generation for Experience users (as validation)
      const isEssentiel = tier === 'essentiel' || tier === 'free' || !tier;
      if ((isEssentiel && newCount % 3 === 0) || (!isEssentiel && newCount === 1)) {
        setTimeout(() => setShowElevationMoment(true), 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const contentTypes = [
    { id: 'post', label: 'Post', icon: Image },
    { id: 'reel', label: 'Reel', icon: Video },
    { id: 'story', label: 'Story', icon: Eye },
  ];

  return (
    <DashboardLayout title="Créer ton contenu">
      <div className="max-w-6xl mx-auto">
        {/* Auto-fill Banner */}
        {hasContextData && !generatedContent && (
          <AutofillBanner
            hasData={hasContextData}
            sources={sources}
            sourcesDisplay={sourcesDisplay}
            message={summaryMessage}
            showDetails={true}
          />
        )}
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-premium p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <FileText className="text-[#E5C585] mb-4" size={28} />
              <h2 className="font-serif text-2xl text-white mb-2">Génère des idées de contenu</h2>
              <p className="text-[#A1A1AA]">
                Crée du contenu engageant avec la méthode POWER LOOK.
              </p>
            </div>
            
            {contentRemaining && (
              <div className="min-w-[200px]">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#A1A1AA]">Ideas this month</span>
                  <span className="text-white">{contentRemaining.used}/{contentRemaining.limit}</span>
                </div>
                <Progress 
                  value={(contentRemaining.used / contentRemaining.limit) * 100} 
                  className="h-2 bg-[#1C1C1C]"
                />
                <p className="text-[#52525B] text-xs mt-2">{contentRemaining.remaining} remaining</p>
              </div>
            )}
          </div>

          {/* Content Type Selection */}
          <div className="mt-8">
            <label className="text-[#A1A1AA] text-sm mb-3 block">Select Content Type</label>
            <div className="flex gap-4">
              {contentTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setContentType(type.id)}
                    className={`flex items-center gap-3 px-6 py-4 border transition-all ${
                      contentType === type.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#E5C585]'
                        : 'border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]'
                    }`}
                    data-testid={`content-type-${type.id}`}
                  >
                    <Icon size={20} />
                    {type.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={loading || (contentRemaining && contentRemaining.remaining <= 0)}
            className="btn-gold px-8 py-4 mt-6 flex items-center gap-2"
            data-testid="generate-content-btn"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Generating...
              </>
            ) : (
              <>
                <Lightbulb size={18} />
                Generate {contentType.charAt(0).toUpperCase() + contentType.slice(1)} Idea
              </>
            )}
          </Button>
        </motion.div>

        {/* Generated Content */}
        {generatedContent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-8 mb-8"
          >
            {/* Interactive AI Buttons */}
            <div className="flex gap-2 mb-6">
              <Button
                variant="outline"
                onClick={handleGenerate}
                disabled={loading}
                className="flex-1 border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] py-3"
              >
                <RefreshCw size={16} className="mr-2" />
                Regenerate
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowRefinementPanel(true)}
                className="flex-1 border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] py-3"
              >
                <Sliders size={16} className="mr-2" />
                Adjust
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowRefinementPanel(true)}
                className="flex-1 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 py-3"
              >
                <MessageSquare size={16} className="mr-2" />
                Ask AI
              </Button>
            </div>

            <div className="mb-6">
              <span className="text-[#E5C585] text-xs font-accent tracking-widest">
                {generatedContent.content_type?.toUpperCase()} IDEA
              </span>
              <h3 className="font-serif text-2xl text-white mt-2">{generatedContent.title}</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="text-[#D4AF37]" size={16} />
                    <span className="text-[#E5C585] text-sm font-accent">HOOK</span>
                  </div>
                  <p className="text-white text-lg italic">"{generatedContent.hook}"</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="text-[#D4AF37]" size={16} />
                    <span className="text-[#E5C585] text-sm font-accent">MAIN IDEA</span>
                  </div>
                  <p className="text-[#A1A1AA] leading-relaxed">{generatedContent.main_idea}</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="text-[#D4AF37]" size={16} />
                    <span className="text-[#E5C585] text-sm font-accent">STORYTELLING ANGLE</span>
                  </div>
                  <p className="text-[#A1A1AA]">{generatedContent.storytelling_angle}</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Image className="text-[#D4AF37]" size={16} />
                    <span className="text-[#E5C585] text-sm font-accent">VISUAL DIRECTION</span>
                  </div>
                  <p className="text-[#A1A1AA]">{generatedContent.visual_direction}</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Hash className="text-[#D4AF37]" size={16} />
                    <span className="text-[#E5C585] text-sm font-accent">HASHTAGS</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {generatedContent.hashtags?.map((tag, i) => (
                      <span 
                        key={i} 
                        className="px-3 py-1 bg-[#D4AF37]/10 text-[#E5C585] text-sm border border-[#D4AF37]/30"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-[#1C1C1C] p-4 border border-[#27272A]">
                  <span className="text-[#E5C585] text-sm font-accent">CALL TO ACTION</span>
                  <p className="text-white mt-2">{generatedContent.call_to_action}</p>
                </div>
              </div>
            </div>

            {/* Script (Pro only) */}
            {generatedContent.script && (
              <div className="mt-8 pt-6 border-t border-[#27272A]">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="text-[#D4AF37]" size={16} />
                  <span className="text-[#E5C585] text-sm font-accent">FULL SCRIPT</span>
                  <span className="text-[10px] font-accent text-[#D4AF37] px-2 py-0.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 ml-2">
                    PRO
                  </span>
                </div>
                <p className="text-[#A1A1AA] leading-relaxed whitespace-pre-wrap">{generatedContent.script}</p>
              </div>
            )}
          </motion.div>
        )}

        {/* History */}
        {history.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="font-serif text-2xl text-white mb-6">Recent Ideas</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {history.slice(0, 6).map((item) => (
                <button
                  key={item.content_id}
                  onClick={() => setGeneratedContent(item)}
                  className="card-premium p-4 text-left hover:border-[#D4AF37]/50 transition-colors"
                  data-testid={`content-history-${item.content_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#E5C585] text-xs font-accent tracking-wider uppercase">
                      {item.content_type}
                    </span>
                    <span className="text-[#52525B] text-xs">
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h4 className="text-white font-serif text-lg mb-2 line-clamp-2">{item.title}</h4>
                  <p className="text-[#A1A1AA] text-sm line-clamp-2">{item.hook}</p>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </div>
      
      {/* AI Refinement Panel */}
      <AIRefinementPanel
        isOpen={showRefinementPanel}
        onClose={() => setShowRefinementPanel(false)}
        toolType="content_generator"
        currentResult={generatedContent}
        onResultUpdate={(newResult) => {
          setGeneratedContent({ ...generatedContent, ...newResult });
          toast.success('Content updated!');
        }}
        suggestedPrompts={[
          "Give me a stronger hook",
          "Make it more emotional and relatable",
          "Add a call-to-action",
          "Give me alternative reel ideas"
        ]}
      />
      
      {/* Elevation Moment - After Content Generation */}
      {showElevationMoment && (
        <ElevationMoment
          trigger="content_generation"
          onClose={() => setShowElevationMoment(false)}
        />
      )}

      {/* Step Review Modal */}
      <StepReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        stepId="content_creation"
        stepName="Créer ton contenu"
        onValidated={() => setShowReviewModal(false)}
      />
    </DashboardLayout>
  );
};

// Wrap with PrerequisiteGate
const ContentGenerator = () => {
  return (
    <PrerequisiteGate 
      toolId="content-generator" 
      toolTitle="Créer ton contenu"
    >
      <ContentGeneratorContent />
    </PrerequisiteGate>
  );
};

export default ContentGenerator;
